import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/components/ThemeProvider";
import { Moon, Sun, Menu, X } from "lucide-react";
import api from "@/lib/api";

const DEFAULT_SECTIONS = [
  { id: "home", label: "Home", visible: true, kind: "section", link_type: "anchor", path: "#home" },
  { id: "about", label: "About", visible: true, kind: "link", link_type: "page", path: "/about-us" },
  { id: "services", label: "Services", visible: true, kind: "section" },
  { id: "bulk-hiring", label: "Bulk Hiring", visible: true, kind: "section" },
  { id: "ai-services", label: "AI IT Services", visible: true, kind: "section" },
  { id: "candidates", label: "Candidates", visible: true, kind: "section" },
  { id: "testimonials", label: "Testimonials", visible: true, kind: "section" },
  { id: "contact", label: "Contact", visible: true, kind: "section" },
  { id: "jobs", label: "Jobs", visible: true, kind: "link", link_type: "page", path: "/jobs" },
];

const LOGO_URL = "/logo.png";

const BeeLogo = () => (
  <div className="flex items-center" data-testid="brand-logo">
    <img
      src={LOGO_URL}
      alt="WorkingBees Global recruitment agency logo"
      className="h-14 w-14 sm:h-16 sm:w-16 object-contain rounded-lg bg-white p-0.5"
    />
  </div>
);

export default function Navbar() {
  const { theme, toggle } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menu, setMenu] = useState(DEFAULT_SECTIONS);
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    api.get("/settings").then((r) => {
      if (r.data?.nav_menu?.length) setMenu(r.data.nav_menu);
    }).catch(() => {});
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToSection = (path) => {
    const id = (path || "").replace(/^\/?#/, "");
    setMobileOpen(false);
    if (onHome) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/#${id}`);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all ${
        scrolled
          ? "bg-background/85 backdrop-blur-md border-b border-border shadow-sm"
          : "bg-background/40 backdrop-blur-sm"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-home-link" className="flex-shrink-0">
          <BeeLogo />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {menu.filter((s) => s.visible).map((s) => (
            (s.link_type || (s.kind === "link" ? "page" : "anchor")) === "page" ? (
              <Link
                key={s.id}
                to={s.path || (s.id === "about" ? "/about-us" : "/jobs")}
                data-testid={`nav-link-${s.id}`}
                className="px-3 py-2 text-sm font-medium text-foreground/75 hover:text-primary transition-colors"
              >
                {s.label}
              </Link>
            ) : (
              <button
                key={s.id}
                onClick={() => goToSection(s.path || `#${s.id}`)}
                data-testid={`nav-section-${s.id}`}
                className="px-3 py-2 text-sm font-medium text-foreground/75 hover:text-primary transition-colors rounded-md"
              >
                {s.label}
              </button>
            )
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            data-testid="theme-toggle-btn"
            aria-label="Toggle theme"
            className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-border hover:bg-secondary transition-colors"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <Button
            onClick={() => goToSection("#contact")}
            data-testid="nav-cta-hire"
            className="hidden md:inline-flex bg-bee-teal text-white hover:bg-[hsl(var(--bee-teal-dark))] rounded-full"
          >
            Hire Talent
          </Button>
          <button
            className="lg:hidden h-9 w-9 inline-flex items-center justify-center rounded-md border border-border"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Open menu"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-border bg-background" data-testid="mobile-menu">
          <div className="px-4 py-3 flex flex-col gap-1">
            {menu.filter((s) => s.visible).map((s) => (
              (s.link_type || (s.kind === "link" ? "page" : "anchor")) === "page" ? (
                <Link
                  key={s.id}
                  to={s.path || (s.id === "about" ? "/about-us" : "/jobs")}
                  onClick={() => setMobileOpen(false)}
                  className="px-3 py-2.5 rounded-md hover:bg-secondary text-sm font-medium"
                  data-testid={`mobile-link-${s.id}`}
                >
                  {s.label}
                </Link>
              ) : (
                <button
                  key={s.id}
                  onClick={() => goToSection(s.path || `#${s.id}`)}
                  data-testid={`mobile-nav-${s.id}`}
                  className="text-left px-3 py-2.5 rounded-md hover:bg-secondary text-sm font-medium"
                >
                  {s.label}
                </button>
              )
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
